import L from "leaflet";
import { useEffect } from "react";
import { useMap } from "react-leaflet";
import { TRANSPARENT_PNG } from "../../utils/common/Constants";
import { MapErrorId, mapTileType } from "../../utils/maps/MapConstants";

const MAX_TILE_RETRIES = 2;
const TILE_RETRY_BACKOFF_MS = 300;

type Props = {
  id: mapTileType;
  url: string;
  refreshInterval?: number;
  layerOpacity?: number;
  setMapApiFailures: (errorID: MapErrorId, isReset?: boolean) => void;
  /** Called when a tile ultimately fails to load (e.g. an expired sig), so the caller can refresh it. */
  onAuthError?: () => void;
} & L.TileLayerOptions;

const TileLayerWithHeader = ({
  id,
  url,
  refreshInterval = 0,
  layerOpacity = 1,
  setMapApiFailures,
  onAuthError,
  ...opts
}: Props) => {
  const map = useMap();

  useEffect(() => {
    const tileCleanups = new Map<HTMLImageElement, () => void>();
    const Layer = L.TileLayer.extend({
      createTile: function (
        coords: L.Coords,
        done: (err: any, tile: HTMLImageElement) => void
      ) {
        const src = (this as any).getTileUrl(coords);

        // Always create an <img> because TileLayer expects HTMLImageElement
        const img = document.createElement("img");
        img.setAttribute("role", "presentation");
        img.alt = "";
        img.decoding = "async";

        // No fetch() here on purpose: auth (sig/expires/subscription-key) travels
        // entirely in the URL now, so we let the <img> load the tile natively.
        // Plain <img> loads aren't subject to CORS (unlike fetch/XHR), so this
        // works even if the tiles endpoint doesn't return an
        // Access-Control-Allow-Origin header.
        let retries = 0;
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const cleanup = () => {
          cancelled = true;
          clearTimeout(retryTimer);
          img.onload = null;
          img.onerror = null;
          tileCleanups.delete(img);
        };
        tileCleanups.set(img, cleanup);

        const loadTile = () => {
          if (cancelled) {
            return;
          }
          img.onload = () => {
            cleanup();
            setMapApiFailures(id, true); // reset error state
            done(null, img);
          };

          img.onerror = () => {
            if (retries < MAX_TILE_RETRIES) {
              retries += 1;
              retryTimer = setTimeout(
                loadTile,
                TILE_RETRY_BACKOFF_MS * retries
              );
              return;
            }

            cleanup();
            onAuthError?.(); // sig may be expired/invalid; ask caller to refresh it
            setMapApiFailures(id);

            img.style.display = "none"; // not strictly needed, but keeps DOM clean
            img.src = TRANSPARENT_PNG;
            done(new Error("Tile failed to load"), img);
          };

          img.src = src;
        };

        loadTile();

        return img;
      },
    });

    const layer = new (Layer as any)(url, {
      ...opts,
      errorTileUrl: TRANSPARENT_PNG,
    }).addTo(map);

    layer.on("tileunload", (event: L.TileEvent) => {
      tileCleanups.get(event.tile)?.();
    });
    layer.setOpacity(layerOpacity);

    // Periodically refresh tiles if `refreshInterval` is provided
    let intervalId: NodeJS.Timeout | null = null;
    if (refreshInterval) {
      intervalId = setInterval(() => {
        layer.redraw();
      }, refreshInterval);
    }

    return () => {
      tileCleanups.forEach((cleanup) => cleanup());
      map.removeLayer(layer);

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [map, url, refreshInterval, setMapApiFailures, onAuthError]);

  // New effect to update opacity without recreating the layer
  useEffect(() => {
    map.eachLayer((l: any) => {
      if (l instanceof L.TileLayer && (l as any)._url === url) {
        l.setOpacity(layerOpacity); // ❗ no redraw, no tile reload
      }
    });
  }, [layerOpacity]);

  return null;
};

export default TileLayerWithHeader;
